/** SovereignRadioPlayer · self-hosted synchronized live station */
import React, { useEffect, useRef, useState } from "react";
import { RadioVisualizer } from "./RadioVisualizer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SovereignRadioPlayer = () => {
  const audioRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | loading | live | off
  const [nowInfo, setNowInfo] = useState(null);
  const [shared, setShared] = useState(false);
  const currentEntry = useRef(null);
  const listenerId = useRef(Math.random().toString(36).slice(2) + Date.now().toString(36));
  const origTitle = useRef(typeof document !== "undefined" ? document.title : "");

  useEffect(() => {
    const title = nowInfo?.current?.title;
    if (status === "live" && title) {
      document.title = `♪ ${title} · NextXus Radio`;
      if ("mediaSession" in navigator) {
        try {
          navigator.mediaSession.metadata = new window.MediaMetadata({
            title,
            artist: "NextXus Radio · The Sovereign Pulse",
            album: nowInfo.current.kind === "talk" ? "Talk Show" : "Music",
            artwork: nowInfo.current.artwork
              ? [{ src: `${API}/radio/artwork/${nowInfo.current.track_id}`, sizes: "512x512", type: "image/jpeg" }]
              : [],
          });
        } catch { /* noop */ }
      }
    } else {
      document.title = origTitle.current;
    }
    return () => { document.title = origTitle.current; };
  }, [status, nowInfo]);

  const shareNow = async () => {
    const url = `${window.location.origin}/radio`;
    const title = nowInfo?.current?.title
      ? `♪ ${nowInfo.current.title} — live on NextXus Radio`
      : "NextXus Radio · Live";
    try {
      if (navigator.share) {
        await navigator.share({ title, text: title, url });
      } else {
        await navigator.clipboard.writeText(`${title} · ${url}`);
        setShared(true);
        setTimeout(() => setShared(false), 2400);
      }
    } catch { /* user cancelled */ }
  };

  const fetchNow = async () => {
    const res = await fetch(`${API}/radio/now?listener=${listenerId.current}`);
    return res.json();
  };

  const playCurrent = async () => {
    setStatus("loading");
    try {
      const data = await fetchNow();
      if (!data.live) { setStatus("off"); setNowInfo(null); return; }
      setNowInfo(data);
      const cur = data.current;
      const a = audioRef.current;
      if (currentEntry.current !== cur.entry_id) {
        currentEntry.current = cur.entry_id;
        a.src = `${API}/radio/stream/${cur.track_id}`;
        await a.play().catch(() => {});
        if (cur.offset_sec > 2 && cur.offset_sec < cur.duration_sec - 2) {
          a.currentTime = cur.offset_sec;
        }
      }
      setStatus("live");
    } catch {
      setStatus("off");
    }
  };

  const tuneIn = () => playCurrent();
  const tuneOut = () => {
    const a = audioRef.current;
    a.pause();
    a.removeAttribute("src");
    currentEntry.current = null;
    setStatus("idle");
  };

  useEffect(() => {
    const a = audioRef.current;
    const onEnded = () => playCurrent();
    a.addEventListener("ended", onEnded);
    return () => a.removeEventListener("ended", onEnded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== "live") return;
    const t = setInterval(async () => {
      const data = await fetchNow().catch(() => null);
      if (data && data.live) setNowInfo(data);
    }, 30000);
    return () => clearInterval(t);
  }, [status]);

  const cur = nowInfo?.current;
  return (
    <div data-testid="sovereign-radio-player" className="w-full" style={{ borderRadius: 12, background: "rgba(4,5,10,0.6)", padding: 24 }}>
      <audio ref={audioRef} preload="none" />
      {status !== "live" ? (
        <div className="text-center py-10">
          <button
            type="button"
            data-testid="radio-tune-in-btn"
            onClick={tuneIn}
            disabled={status === "loading"}
            className="font-mono uppercase tracking-[0.26em]"
            style={{
              minHeight: 56, padding: "0 42px", fontSize: 16, fontWeight: 700,
              color: "#0a0a0f", background: "linear-gradient(135deg, #D4AF37 0%, #FFD700 100%)",
              border: "none", borderRadius: 999, cursor: "pointer",
              boxShadow: "0 0 34px rgba(255,215,0,0.4)",
            }}
          >
            {status === "loading" ? "Tuning…" : "▶ Tune In · Live"}
          </button>
          {status === "off" && (
            <p className="mt-4 font-mono uppercase tracking-[0.2em]" style={{ fontSize: 11, color: "rgba(224,224,224,0.55)" }}>
              The station is quiet — no tracks in the library yet.
            </p>
          )}
        </div>
      ) : (
        <div>
          <RadioVisualizer audioRef={audioRef} active={status === "live"} />
          <div className="flex items-center gap-3 flex-wrap">
            {cur?.artwork && (
              <img
                data-testid="radio-now-artwork"
                src={`${API}/radio/artwork/${cur.track_id}`}
                alt={cur?.title}
                style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8, border: "1px solid rgba(255,215,0,0.5)" }}
              />
            )}
            <span
              className="font-mono uppercase tracking-[0.2em]"
              style={{
                fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 999,
                color: "#0a0a0f",
                background: cur?.kind === "talk" ? "#00FFCC" : cur?.kind === "jingle" ? "#e0e0e0" : "#FFD700",
              }}
            >
              {cur?.kind === "talk" ? "◉ Talk Show" : cur?.kind === "jingle" ? "♬ Station ID" : "♪ Music"}
            </span>
            <span data-testid="radio-now-playing" className="font-serif" style={{ fontSize: 20, color: "#FFD700" }}>
              {cur?.title}
            </span>
            {nowInfo?.listeners > 0 && (
              <span
                data-testid="radio-listener-count"
                className="font-mono uppercase tracking-[0.18em]"
                title="Listeners tuned in right now"
                style={{ fontSize: 10, color: "#00FFCC", border: "1px solid rgba(0,255,204,0.4)", borderRadius: 999, padding: "4px 10px" }}
              >
                ◉ {nowInfo.listeners} tuned in
              </span>
            )}
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                data-testid="radio-share-now-btn"
                onClick={shareNow}
                title="Share what's playing"
                className="font-mono uppercase tracking-[0.2em]"
                style={{
                  minHeight: 44, padding: "0 16px", fontSize: 11,
                  color: shared ? "#0a0a0f" : "#00FFCC",
                  background: shared ? "#00FFCC" : "transparent",
                  border: "1px solid rgba(0,255,204,0.5)",
                  borderRadius: 8, cursor: "pointer",
                  transition: "background-color 160ms ease, color 160ms ease",
                }}
              >
                {shared ? "✓ Copied" : "⤴ Share"}
              </button>
              <button
                type="button"
                data-testid="radio-tune-out-btn"
                onClick={tuneOut}
                className="font-mono uppercase tracking-[0.2em]"
                style={{
                  minHeight: 44, padding: "0 16px", fontSize: 11, color: "#FFD700",
                  background: "transparent", border: "1px solid rgba(255,215,0,0.5)",
                  borderRadius: 8, cursor: "pointer",
                }}
              >
                ■ Tune Out
              </button>
            </div>
          </div>
          {nowInfo?.upcoming?.length > 0 && (
            <div className="mt-5" style={{ borderTop: "1px dashed rgba(255,215,0,0.25)", paddingTop: 12 }}>
              <p className="font-mono uppercase tracking-[0.24em] mb-2" style={{ fontSize: 10, color: "rgba(224,224,224,0.5)" }}>
                Up Next
              </p>
              {nowInfo.upcoming.slice(0, 3).map((u) => (
                <p key={u.entry_id} className="font-mono" style={{ fontSize: 13, color: "#e0e0e0", margin: "4px 0" }}>
                  <span style={{ color: u.kind === "talk" ? "#00FFCC" : u.kind === "jingle" ? "#e0e0e0" : "#FFD700" }}>{u.kind === "talk" ? "◉" : u.kind === "jingle" ? "♬" : "♪"}</span>{" "}
                  {u.title}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SovereignRadioPlayer;
